import React, { useEffect, useState, createElement } from 'react';
import { ArrowLeftIcon, FileTextIcon, AlertTriangleIcon, CheckCircle2Icon, ArrowRightIcon, MailIcon, CalendarIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
declare global {
  interface Window {
    Tally?: {
      openPopup: (formId: string, options?: Record<string, any>) => void;
      closePopup: (formId: string) => void;
    };
  }
}
export function InvestPage() {
  const [tallyReady, setTallyReady] = useState(false);
  useEffect(() => {
    // Check if Tally is already loaded
    if (window.Tally) {
      setTallyReady(true);
      return;
    }
    const loadTally = () => {
      const existing = document.querySelector('script[src="https://tally.so/widgets/embed.js"]');
      if (existing) {
        // Script tag exists but may not have loaded yet — poll for it
        const interval = setInterval(() => {
          if (window.Tally) {
            setTallyReady(true);
            clearInterval(interval);
          }
        }, 200);
        // Stop polling after 10 seconds
        setTimeout(() => clearInterval(interval), 10000);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://tally.so/widgets/embed.js';
      script.async = true;
      script.onload = () => {
        // Tally may take a moment to initialize after script loads
        const interval = setInterval(() => {
          if (window.Tally) {
            setTallyReady(true);
            clearInterval(interval);
          }
        }, 100);
        setTimeout(() => clearInterval(interval), 5000);
      };
      document.head.appendChild(script);
    };
    loadTally();
  }, []);
  const openInvestorForm = () => {
    if (window.Tally) {
      window.Tally.openPopup('44258r', {
        layout: 'modal',
        width: 700,
        autoClose: 5000
      });
    } else {
      // Fallback: open Tally form in new tab if widget didn't load
      window.open('https://tally.so/r/44258r', '_blank');
    }
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-[#111111]" data-id="element-3777">
      {/* Private Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50" data-id="element-3778">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between" data-id="element-3779">
          <div className="flex items-center space-x-2 md:space-x-4 min-w-0" data-id="element-3780">
            <Link to="/investor-portal" className="text-gray-500 hover:text-[#631bb7] transition-colors shrink-0" data-id="element-3781">
              <ArrowLeftIcon size={18} data-id="element-3782" />
            </Link>
            <div className="h-5 w-px bg-gray-200 shrink-0" data-id="element-3783"></div>
            <img src="https://cdn.magicpatterns.com/uploads/u2RsmpFkExbV8oAVAJrtqh/Caveo-Lockup-FC.png" alt="Caveo" className="h-5 md:h-6 shrink-0" data-id="element-3784" />
            <span className="bg-gray-100 text-gray-600 text-[10px] md:text-xs font-semibold px-1.5 md:px-2 py-0.5 md:py-1 rounded-full uppercase tracking-wide shrink-0" data-id="element-3785">
              Investor Portal
            </span>
          </div>
          <div className="text-xs md:text-sm text-gray-500 shrink-0 hidden sm:block" data-id="element-3786">
            Confidential & Proprietary
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12 space-y-10" data-id="element-3787">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto space-y-4" data-id="element-3788">
          <h1 className="text-[2rem] md:text-[3.25rem] font-semibold text-[#111111] leading-[1.15] tracking-tight" data-id="element-3789">
            Ready to Invest in Caveo?
          </h1>
        </div>

        {/* Personal Note */}
        <div className="max-w-2xl mx-auto" data-id="element-3790">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8" data-id="element-3791">
            <p className="text-base text-[#111111] leading-relaxed" data-id="element-3792">
              If you made it here, thank you. This round is built on trust, not
              transactions. Take your time with the materials below, and reach
              out anytime if something is unclear or you just want to talk it
              through.
            </p>
            <p className="text-[15px] text-[#111111] mt-4" data-id="element-3793"></p>
            <div className="mt-4 flex items-center gap-3" data-id="element-3794">
              <div className="w-px h-8 bg-[#631bb7]" data-id="element-3795"></div>
              <div data-id="element-3796">
                <p className="font-semibold text-[#111111] text-sm" data-id="element-3797">
                  Jason Phillips
                </p>
                <p className="text-xs text-gray-500" data-id="element-3798">Founder & CEO</p>
              </div>
            </div>
          </div>
        </div>

        {/* Deal Terms Summary */}
        <div className="max-w-2xl mx-auto" data-id="element-3799">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3800">
            <div className="px-6 py-4 border-b border-gray-100 bg-[#f8f5fc]" data-id="element-3801">
              <h2 className="text-[1.625rem] md:text-[2.5rem] font-semibold text-[#111111] leading-[1.2]" data-id="element-3802">
                The Offer at a Glance
              </h2>
            </div>
            <div className="px-6 py-5" data-id="element-3803">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3804">
                <div data-id="element-3805">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-3806">
                    Round Size
                  </p>
                  <p className="text-lg font-bold text-[#111111]" data-id="element-3807">$275,000</p>
                </div>
                <div data-id="element-3808">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-3809">
                    Instrument
                  </p>
                  <p className="text-lg font-bold text-[#111111]" data-id="element-3810">SAFE</p>
                  <p className="text-xs text-gray-500" data-id="element-3811">
                    Simple Agreement for Future Equity
                  </p>
                </div>
                <div data-id="element-3812">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-3813">
                    Friends & Family Discount
                  </p>
                  <p className="text-lg font-bold text-[#111111]" data-id="element-3814">25%</p>
                </div>
                <div data-id="element-3815">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-3816">
                    Minimum Investment
                  </p>
                  <p className="text-lg font-bold text-[#111111]" data-id="element-3817">$10,000</p>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-gray-100 flex items-center gap-2" data-id="element-3818">
                <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-3819"></div>
                <p className="text-sm font-semibold text-green-700" data-id="element-3820">
                  Round Status: Open
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Process Steps */}
        <div className="max-w-2xl mx-auto" data-id="element-3821">
          <div className="space-y-0" data-id="element-3822">
            <div className="flex gap-4" data-id="element-3823">
              <div className="flex flex-col items-center" data-id="element-3824">
                <div className="w-8 h-8 rounded-full bg-[#631bb7] text-white flex items-center justify-center font-bold text-sm shrink-0" data-id="element-3825">
                  1
                </div>
                <div className="w-0.5 bg-gray-200 flex-1 my-1" data-id="element-3826"></div>
              </div>
              <div className="pb-6" data-id="element-3827">
                <p className="text-lg font-semibold text-[#111111]" data-id="element-3828">
                  Review the disclosures below.
                </p>
              </div>
            </div>
            <div className="flex gap-4" data-id="element-3829">
              <div className="flex flex-col items-center" data-id="element-3830">
                <div className="w-8 h-8 rounded-full bg-[#631bb7] text-white flex items-center justify-center font-bold text-sm shrink-0" data-id="element-3831">
                  2
                </div>
                <div className="w-0.5 bg-gray-200 flex-1 my-1" data-id="element-3832"></div>
              </div>
              <div className="pb-6" data-id="element-3833">
                <p className="text-lg font-semibold text-[#111111]" data-id="element-3834">
                  Please review the SAFE agreement.
                </p>
              </div>
            </div>

            <div className="flex gap-4" data-id="element-3835">
              <div className="flex flex-col items-center" data-id="element-3836">
                <div className="w-8 h-8 rounded-full bg-[#631bb7] text-white flex items-center justify-center font-bold text-sm shrink-0" data-id="element-3837">
                  3
                </div>
              </div>
              <div data-id="element-3838">
                <p className="text-lg font-semibold text-[#111111]" data-id="element-3839">
                  Once you are ready, click the "Invest Now" button below. We
                  will collect your information and send you an agreement within
                  48 hours.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Important Disclosures */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden max-w-4xl mx-auto" data-id="element-3840">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-amber-50" data-id="element-3841">
            <AlertTriangleIcon size={20} className="text-amber-600" data-id="element-3842" />
            <h2 className="text-[1.625rem] md:text-[2.5rem] font-semibold text-[#111111] leading-[1.2]" data-id="element-3843">
              Important Disclosures
            </h2>
          </div>
          <div className="px-6 py-6 space-y-4 text-gray-600 leading-relaxed text-sm" data-id="element-3844">
            <p data-id="element-3845"></p>
            <p data-id="element-3846">
              Investments in AppEmbrace, Inc. (d/b/a Caveo) are available only
              to accredited investors as defined under Rule 501 of Regulation D
              of the Securities Act of 1933, as amended. An accredited investor
              generally includes individuals with an annual income exceeding
              $200,000 (or $300,000 jointly with a spouse) in each of the two
              most recent years, or a net worth exceeding $1,000,000, excluding
              the value of their primary residence.
            </p>
            <p data-id="element-3847">
              Investing in early-stage companies involves a high degree of risk.
              You may lose your entire investment. These securities are
              illiquid, meaning there is no guaranteed market for resale. Past
              performance of any company or investment is not indicative of
              future results.
            </p>
            <p data-id="element-3848"></p>
            <p className="font-medium text-gray-700" data-id="element-3849">
              This offering has not been reviewed or approved by the U.S.
              Securities and Exchange Commission or any state securities
              regulator.
            </p>
          </div>
        </div>

        {/* Embedded SAFE Agreement PDF */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden max-w-4xl mx-auto" data-id="element-3850">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50" data-id="element-3851">
            <FileTextIcon size={20} className="text-[#631bb7]" data-id="element-3852" />
            <h2 className="text-[1.625rem] md:text-[2.5rem] font-semibold text-[#111111] leading-[1.2]" data-id="element-3853">
              SAFE Agreement
            </h2>
          </div>
          <div className="w-full" style={{
          height: '80vh'
        }} data-id="element-3854">
            <iframe src="https://drive.google.com/file/d/1w59cWXbjVzlR-My86Eke0EnrsG9-xluU/preview" className="w-full h-full border-0" allow="autoplay" title="Caveo SAFE Agreement" data-id="element-3855" />
          </div>
        </div>

        {/* Contact, Calendar & Invest */}
        <div className="max-w-md mx-auto" data-id="element-3856">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8 text-center" data-id="element-3857">
            <h2 className="text-[1.625rem] md:text-[2.5rem] font-semibold text-[#111111] leading-[1.2] mb-2" data-id="element-3858">
              Have Questions Before You Commit?
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-6" data-id="element-3859">
              Email me or book a meeting.
            </p>
            <div className="flex flex-col gap-3" data-id="element-3860">
              <a href="mailto:jbp@caveohq.com" className="w-full inline-flex items-center justify-center gap-2 bg-white text-[#631bb7] border border-[#631bb7] px-6 py-3 rounded-lg font-medium text-sm hover:bg-[#f8f5fc] transition-colors no-underline" data-id="element-3861">
                <MailIcon size={16} data-id="element-3862" />
                Email Me
              </a>
              <a href="https://calendar.app.google/EnoqEH7z4cY89jjN8" target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 bg-white text-[#631bb7] border border-[#631bb7] px-6 py-3 rounded-lg font-medium text-sm hover:bg-[#f8f5fc] transition-colors no-underline" data-id="element-3863">
                <CalendarIcon size={16} data-id="element-3864" />
                Book a Meeting
              </a>
              <div className="pt-3 mt-1 border-t border-gray-100" data-id="element-3865">
                <button onClick={openInvestorForm} className="w-full inline-flex items-center justify-center bg-[#631bb7] text-white px-8 py-3.5 rounded-lg font-medium hover:bg-[#521099] transition-colors text-base shadow-sm cursor-pointer border-0" data-id="element-3866">
                  Invest Now
                  <ArrowRightIcon size={20} className="ml-2" data-id="element-3867" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 py-8 mt-12" data-id="element-3868">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-gray-500" data-id="element-3869">
          <p data-id="element-3870">&copy; 2026 AppEmbrace, Inc. DBA Caveo. All rights reserved.</p>
          <p className="mt-2 text-xs text-gray-400" data-id="element-3871">
            This page contains confidential information intended only for
            authorized investors. Do not share or distribute without permission.
          </p>
        </div>
      </footer>
    </div>;
}